import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import Markdown from "markdown-to-jsx";
import { HTMLProps, useEffect, useMemo, useState } from "react";
import useTypingEffect from "../hook/typing-effect";
import { Product } from "../types";
import { cloneNode } from "../ui-script/utils";
import { ProductDetails } from "@/sdk/types";
import { filterFAQs } from "./faq-util";
import { useElementVisibility } from "../hook/use-element-visibility";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useCarousel,
} from "@/components/ui/carousel";
import { useRef } from "react";
export interface FAQ {
  answer: any;
  category: string;
  type: "text" | "product-list";
  question: string;
  intent: {
    primary: string;
    secondary: string;
    other: string[];
  };
}
[];
const DURATION = 50;
export function FaqPillet({
  faqs,
  className,
}: {
  faqs: FAQ[];
  className: HTMLProps<HTMLElement>["className"];
}) {
  const filteredFAQs = useMemo(() => {
    if (!faqs.length || !("metadata" in faqs[0])) return faqs;
    return filterFAQs(faqs, window.helium?.finalResponse);
  }, [faqs]);

  const [selected, setSelected] = useState<null | number>(null);
  const questions = filteredFAQs.map((faq) => faq.question);
  const [elementRef] = useElementVisibility<HTMLDivElement>({
    data: {
      name: "faq",
    },
  });

  const scrollToRight = (element: HTMLDivElement | null) => {
    element.scrollTo({
      left: element.scrollLeft + 200, // Add 100px to the current scroll position
      behavior: "smooth", // Enable smooth scrolling
    });
  };

  if (questions.length === 0) return null;
  return (
    <>
      <div className="hp-w-full hp-mx-auto">
        <div className="hp-my-2 hp-flex hp-flex-row hp-items-center hp-justify-between hp-text-2xl hp-font-medium">
          Most Asked Questions
          <div
            className="hp-flex hp-h-8 hp-w-8 hp-cursor-pointer hp-items-center hp-justify-center hp-rounded-full hp-bg-white hp-p-1 hover:hp-bg-white"
            onClick={() => {
              console.log("running");
              scrollToRight(elementRef.current);
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.7833 8.66666H2.66667V7.33332H10.7833L7.05 3.59999L8 2.66666L13.3333 7.99999L8 13.3333L7.05 12.4L10.7833 8.66666Z"
                fill="#1D1B20"
              />
            </svg>
          </div>
        </div>
        <div
          ref={elementRef}
          className="no-scrollbar hp-relative hp-h-full hp-w-full hp-overflow-x-auto hp-p-2"
        >
          <div className={cn(`hp-grid hp-grid-flow-col hp-gap-3`, className)}>
            {questions.map((question, index) => (
              <div
                onClick={() => {
                  window?.trackHeliumEvent?.({
                    name: "faq-clicked",
                    properties: {
                      handle: window.location.pathname.split("/").at(-1),
                      question: question,
                    },
                  });
                  setSelected(index);
                }}
                style={{
                  background: "#f1f1f1",
                }}
                className={cn(
                  `hp-text-normal hp-line-clamp-2 hp-w-[250px] hp-cursor-pointer hp-items-center hp-rounded hp-border hp-border-dotted hp-border-[#ABABAB80] hp-px-2.5 hp-py-3 hp-text-[12px] hp-font-medium`,
                  selected === index
                    ? "hp-bg-primary hp-text-primary-foreground"
                    : "hp-text-black",
                )}
              >
                <div className="hp-line-clamp-2">{question}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <FaqBottomSheet
        selected={selected}
        setSelected={setSelected}
        faqs={filteredFAQs}
        content={selected !== null ? filteredFAQs[selected]?.answer : ""}
      />
    </>
  );
}

const TextToShow = ({ children }: { children: string }) => {
  const content = useTypingEffect(children, DURATION, false, {
    //fontFamily: "Montserrat, serif",
    //fontWeight: 600,
  });
  return content;
};
export function FaqBottomSheet({
  selected,
  setSelected,
  faqs,
  content,
}: {
  selected: number | null;
  setSelected: React.Dispatch<React.SetStateAction<number | null>>;
  faqs: FAQ[];
  content: string | { color: string; material: string; pattern: string };
  typingSpeed?: number;
}) {
  const shownRef = useRef({});
  const [products, setProducts] = useState<ProductDetails[]>([]);
  const [uiProducts, setUiProducts] = useState([]);

  useEffect(() => {
    if (typeof content === "string") return;
    async function getProductHandles() {
      const filters = {
        ...(content?.color && { primaryColor: content.color }),
        ...(content?.material && { material: content.material }),
        ...(content?.pattern && { pattern: content.pattern }),
      };

      const products: string[] = await window.helium.datastore.search({
        filterKeys: filters,
      });
      const details = await Promise.all(
        products.map(async (product) => {
          const data = await window.helium.datastore.getProduct({
            id: product,
          });
          return data.details;
        }),
      );
      setProducts(details);
    }
    getProductHandles();
  }, []);

  const renderCloneNode = async (item: Product) => {
    const templateNode = window.helium.datastore.memory.html.product;
    const handle =
      window.helium.datastore.memory.html.productHandle ||
      window.location.pathname.split("/").at(-1);
    if (!templateNode) return null;

    let existingProduct = (
      await window.helium.datastore.getProduct({
        id: handle ?? "",
      })
    ).details;

    if (window.location.pathname.includes("product")) {
      existingProduct = window.helium.datastore.memory.product["test"].details;
    }
    const node = cloneNode(templateNode, item, existingProduct);
    node.className += " !w-[12rem]";
    return { __html: node.outerHTML };
  };

  useEffect(() => {
    const fetchRenderedProducts = async () => {
      const productHtmls = await Promise.all(
        products?.map((item) => renderCloneNode(item)),
      );
      setUiProducts(productHtmls.map((html) => html.__html));
    };

    fetchRenderedProducts();
  }, [products]);

  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  useEffect(() => {
    shownRef.current[selected] = true;
    shownRef.current[selectedIndex] = true;
  }, [selected, selectedIndex]);

  useEffect(() => {
    if (selected !== null) {
      setSelectedIndex(selected);
    }
  }, [selected, faqs]);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      setSelectedIndex(carouselApi.selectedScrollSnap());
    };

    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  useEffect(() => {
    // Sync the carousel with selectedIndex when it changes
    if (carouselApi && selectedIndex !== undefined) {
      carouselApi.scrollTo(selectedIndex);
    }
  }, [selectedIndex, carouselApi]);

  const handleNext = () => {
    carouselApi?.scrollNext();
  };

  const handlePrev = () => {
    carouselApi?.scrollPrev();
  };

  return selected !== null ? (
    <Drawer
      open={selected !== null}
      onOpenChange={(open) => {
        if (!open) setSelected(null);
      }}
    >
      <DrawerContent
        faqProps={
          "hp-absolute hp-left-[50%] hp-w-12 hp-h-12 hp-right-4 hp-top-[-50px] hp-p-1 hp-z-10 hp-flex hp-translate-x-[-50%] hp-items-center hp-justify-center hp-rounded-full hp-bg-gray-500 hp-text-white"
        }
      >
        <div className="hp-mx-auto hp-w-full hp-max-w-2xl">
          <div className="hp-flex hp-w-full hp-gap-2 hp-px-4">
            {faqs.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "hp-p-0.5 hp-flex-grow hp-rounded hp-transition-all hp-duration-300",
                )}
                style={{
                  background:
                    selectedIndex === index
                      ? "linear-gradient(244.9deg, #000AFF -8.94%, #B145C2 20.23%, #FC9D67 63.78%, #A6A9FA 107.73%)"
                      : "#F1F1F1",
                }}
              />
            ))}
          </div>
          <Carousel
            className="hp-mx-auto"
            setApi={setCarouselApi}
            opts={{ loop: true }}
          >
            <CarouselContent>
              {faqs.map((faq, index) => (
                <CarouselItem key={index}>
                  <div className="hp-mt-4 hp-flex hp-items-center hp-justify-center">
                    <Logo />
                  </div>
                  <DrawerHeader>
                    <DrawerTitle
                      className="hp-text-center hp-text-2xl"
                      style={{
                        //fontFamily: "Montserrat, serif",
                        //fontOpticalSizing: "auto",
                        fontWeight: 600,
                        fontSize: "20px",
                        //fontStyle: "normal",
                      }}
                    >
                      {faq.question}
                    </DrawerTitle>
                  </DrawerHeader>
                  <div className="hp-px-4 hp-pb-0 hp-mt-2">
                    <div className="no-scrollbar hp-max-h-[60vh] hp-overflow-y-auto hp-pb-4">
                      {typeof faq.question === "string" ? (
                        <div
                          style={{
                            lineHeight: "18px",
                          }}
                          className="hp-text-center hp-text-xl hp-text-gray-800"
                        >
                          {selectedIndex === index ||
                          shownRef.current[index] ? (
                            <TextToShow key={selected}>{faq.answer}</TextToShow>
                          ) : (
                            <></>
                          )}
                        </div>
                      ) : uiProducts?.length > 0 ? (
                        <div className="hp-grid hp-grid-cols-2 hp-gap-x-2 hp-gap-y-2 md:hp-grid-cols-4">
                          {uiProducts.map((html, i) => (
                            <div
                              key={i}
                              dangerouslySetInnerHTML={{ __html: html }}
                              className="hp-w-[200px]"
                            />
                          ))}
                        </div>
                      ) : (
                        <div>No content available</div>
                      )}
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="hp-flex hp-justify-between hp-gap-x-1 hp-px-4 hp-pb-4">
              <Button
                className="hp-border hp-w-full hp-rounded-none hp-bg-white hp-text-black hp-py-4"
                variant={"secondary"}
                onClick={() => handlePrev()}
              >
                Previous
              </Button>
              <Button
                className="hp-w-full hp-rounded-none hp-border hp-py-4 hp-border-transparent hp-bg-black hp-text-white"
                onClick={() => handleNext()}
              >
                Next
              </Button>
            </div>
          </Carousel>
        </div>
      </DrawerContent>
    </Drawer>
  ) : null;
}

export function Logo() {
  return (
    <svg
      className="mx-auto"
      width="60"
      height="60"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g style="mix-blend-mode:plus-darker" filter="url(#filter0_f_1058_474)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.1415 37.8392C59.6784 36.417 57.7855 35.0451 55.5537 33.7915C55.4257 34.3412 55.2862 34.8967 55.1353 35.4571C57.6921 36.9001 59.8156 38.4905 61.2498 40.0929C61.2368 43.1147 60.5932 45.9875 59.4437 48.5867C56.8648 49.5829 53.1902 49.9894 49.0832 49.8666C48.6271 50.6546 48.1597 51.4207 47.683 52.1633C51.7996 52.2555 55.4668 51.7678 58.3194 50.7751C54.6243 57.0438 47.8033 61.2499 40 61.2499C39.0315 61.2499 38.0782 61.1851 37.1441 61.0596C41.6654 58.193 46.532 52.3113 50.1451 44.765C53.7026 37.3348 55.241 29.9698 54.713 24.6672C58.2543 28.0663 60.6206 32.6803 61.1415 37.8392ZM52.8354 23.0628C50.4974 21.2883 47.7808 19.9868 44.8281 19.3007C40.2467 21.8852 34.8475 28.4137 31.3314 35.7572C26.2387 46.3939 29.2293 50.1098 34.0158 52.4016C38.8023 54.6933 42.7975 54.3221 47.8902 43.6854C51.4239 36.3049 53.4965 28.1374 52.8354 23.0628ZM40 14.9999C43.495 14.9999 46.8225 15.7171 49.8433 17.0123C50.1708 17.0965 50.4882 17.2115 50.7943 17.358C51.0965 17.5028 51.3816 17.6753 51.6495 17.8743C59.5886 22.063 65 30.3995 65 39.9999C65 53.807 53.8071 64.9999 40 64.9999C26.1929 64.9999 15 53.807 15 39.9999C15 38.2384 15.1822 36.5194 15.5287 34.8608C15.5483 34.7186 15.5739 34.577 15.6055 34.4361C15.6397 34.2836 15.6807 34.1334 15.7282 33.9854C18.4207 23.0834 28.2663 14.9999 40 14.9999ZM21.8211 28.9898C25.547 22.8511 32.2945 18.7499 40 18.7499C40.8843 18.7499 41.7561 18.8039 42.612 18.9088C38.1593 21.829 33.4004 27.6357 29.8513 35.0485C26.2481 42.5741 24.7162 50.0329 25.3047 55.3496C21.7067 51.904 19.3179 47.2059 18.8388 41.9558C20.2881 43.3686 22.1615 44.732 24.3702 45.9795C24.8287 44.08 25.4227 42.1146 26.1515 40.1157C26.0865 39.1016 26.2251 38.022 26.4822 36.8753C27.0684 34.2612 28.1109 32.0119 30.48 30.7572C31.0901 29.6813 31.7212 28.6447 32.3689 27.6513C28.2986 27.5553 24.6654 28.0255 21.8211 28.9898ZM29.9426 45.5922C30.1166 47.1228 30.728 48.2828 31.6223 49.2073C33.4901 49.8548 35.471 50.4205 37.5379 50.8841C38.6438 51.1321 39.74 51.3431 40.8214 51.5182C41.6228 51.064 42.4312 50.3683 43.2606 49.3747C41.542 49.1412 39.8033 48.83 38.0849 48.4446C34.4109 47.6207 31.7817 46.6714 29.9426 45.5922ZM42.1023 30.5307C44.9993 31.1804 47.8468 32.0984 50.4676 33.1911C50.5975 32.6246 50.7121 32.0672 50.8105 31.5214C48.2623 30.4846 45.4519 29.6 42.4614 28.9294C40.8047 28.5578 39.1699 28.2693 37.5742 28.0602C37.1482 28.5885 36.7277 29.1386 36.3152 29.7072C37.9544 29.7669 39.8669 30.0294 42.1023 30.5307Z"
          fill="url(#paint0_linear_1058_474)"
        />
      </g>
      <g style="mix-blend-mode:plus-darker" filter="url(#filter1_f_1058_474)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.1415 37.8392C59.6784 36.417 57.7855 35.0451 55.5537 33.7915C55.4257 34.3412 55.2862 34.8967 55.1353 35.4571C57.6921 36.9001 59.8156 38.4905 61.2498 40.0929C61.2368 43.1147 60.5932 45.9875 59.4437 48.5867C56.8648 49.5829 53.1902 49.9894 49.0832 49.8666C48.6271 50.6546 48.1597 51.4207 47.683 52.1633C51.7996 52.2555 55.4668 51.7678 58.3194 50.7751C54.6243 57.0438 47.8033 61.2499 40 61.2499C39.0315 61.2499 38.0782 61.1851 37.1441 61.0596C41.6654 58.193 46.532 52.3113 50.1451 44.765C53.7026 37.3348 55.241 29.9698 54.713 24.6672C58.2543 28.0663 60.6206 32.6803 61.1415 37.8392ZM52.8354 23.0628C50.4974 21.2883 47.7808 19.9868 44.8281 19.3007C40.2467 21.8852 34.8475 28.4137 31.3314 35.7572C26.2387 46.3939 29.2293 50.1098 34.0158 52.4016C38.8023 54.6933 42.7975 54.3221 47.8902 43.6854C51.4239 36.3049 53.4965 28.1374 52.8354 23.0628ZM40 14.9999C43.495 14.9999 46.8225 15.7171 49.8433 17.0123C50.1708 17.0965 50.4882 17.2115 50.7943 17.358C51.0965 17.5028 51.3816 17.6753 51.6495 17.8743C59.5886 22.063 65 30.3995 65 39.9999C65 53.807 53.8071 64.9999 40 64.9999C26.1929 64.9999 15 53.807 15 39.9999C15 38.2384 15.1822 36.5194 15.5287 34.8608C15.5483 34.7186 15.5739 34.577 15.6055 34.4361C15.6397 34.2836 15.6807 34.1334 15.7282 33.9854C18.4207 23.0834 28.2663 14.9999 40 14.9999ZM21.8211 28.9898C25.547 22.8511 32.2945 18.7499 40 18.7499C40.8843 18.7499 41.7561 18.8039 42.612 18.9088C38.1593 21.829 33.4004 27.6357 29.8513 35.0485C26.2481 42.5741 24.7162 50.0329 25.3047 55.3496C21.7067 51.904 19.3179 47.2059 18.8388 41.9558C20.2881 43.3686 22.1615 44.732 24.3702 45.9795C24.8287 44.08 25.4227 42.1146 26.1515 40.1157C26.0865 39.1016 26.2251 38.022 26.4822 36.8753C27.0684 34.2612 28.1109 32.0119 30.48 30.7572C31.0901 29.6813 31.7212 28.6447 32.3689 27.6513C28.2986 27.5553 24.6654 28.0255 21.8211 28.9898ZM29.9426 45.5922C30.1166 47.1228 30.728 48.2828 31.6223 49.2073C33.4901 49.8548 35.471 50.4205 37.5379 50.8841C38.6438 51.1321 39.74 51.3431 40.8214 51.5182C41.6228 51.064 42.4312 50.3683 43.2606 49.3747C41.542 49.1412 39.8033 48.83 38.0849 48.4446C34.4109 47.6207 31.7817 46.6714 29.9426 45.5922ZM42.1023 30.5307C44.9993 31.1804 47.8468 32.0984 50.4676 33.1911C50.5975 32.6246 50.7121 32.0672 50.8105 31.5214C48.2623 30.4846 45.4519 29.6 42.4614 28.9294C40.8047 28.5578 39.1699 28.2693 37.5742 28.0602C37.1482 28.5885 36.7277 29.1386 36.3152 29.7072C37.9544 29.7669 39.8669 30.0294 42.1023 30.5307Z"
          fill="url(#paint1_linear_1058_474)"
        />
      </g>
      <g style="mix-blend-mode:color-burn" filter="url(#filter2_f_1058_474)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.1415 37.8392C59.6784 36.417 57.7855 35.0451 55.5537 33.7915C55.4257 34.3412 55.2862 34.8967 55.1353 35.4571C57.6921 36.9001 59.8156 38.4905 61.2498 40.0929C61.2368 43.1147 60.5932 45.9875 59.4437 48.5867C56.8648 49.5829 53.1902 49.9894 49.0832 49.8666C48.6271 50.6546 48.1597 51.4207 47.683 52.1633C51.7996 52.2555 55.4668 51.7678 58.3194 50.7751C54.6243 57.0438 47.8033 61.2499 40 61.2499C39.0315 61.2499 38.0782 61.1851 37.1441 61.0596C41.6654 58.193 46.532 52.3113 50.1451 44.765C53.7026 37.3348 55.241 29.9698 54.713 24.6672C58.2543 28.0663 60.6206 32.6803 61.1415 37.8392ZM52.8354 23.0628C50.4974 21.2883 47.7808 19.9868 44.8281 19.3007C40.2467 21.8852 34.8475 28.4137 31.3314 35.7572C26.2387 46.3939 29.2293 50.1098 34.0158 52.4016C38.8023 54.6933 42.7975 54.3221 47.8902 43.6854C51.4239 36.3049 53.4965 28.1374 52.8354 23.0628ZM40 14.9999C43.495 14.9999 46.8225 15.7171 49.8433 17.0123C50.1708 17.0965 50.4882 17.2115 50.7943 17.358C51.0965 17.5028 51.3816 17.6753 51.6495 17.8743C59.5886 22.063 65 30.3995 65 39.9999C65 53.807 53.8071 64.9999 40 64.9999C26.1929 64.9999 15 53.807 15 39.9999C15 38.2384 15.1822 36.5194 15.5287 34.8608C15.5483 34.7186 15.5739 34.577 15.6055 34.4361C15.6397 34.2836 15.6807 34.1334 15.7282 33.9854C18.4207 23.0834 28.2663 14.9999 40 14.9999ZM21.8211 28.9898C25.547 22.8511 32.2945 18.7499 40 18.7499C40.8843 18.7499 41.7561 18.8039 42.612 18.9088C38.1593 21.829 33.4004 27.6357 29.8513 35.0485C26.2481 42.5741 24.7162 50.0329 25.3047 55.3496C21.7067 51.904 19.3179 47.2059 18.8388 41.9558C20.2881 43.3686 22.1615 44.732 24.3702 45.9795C24.8287 44.08 25.4227 42.1146 26.1515 40.1157C26.0865 39.1016 26.2251 38.022 26.4822 36.8753C27.0684 34.2612 28.1109 32.0119 30.48 30.7572C31.0901 29.6813 31.7212 28.6447 32.3689 27.6513C28.2986 27.5553 24.6654 28.0255 21.8211 28.9898ZM29.9426 45.5922C30.1166 47.1228 30.728 48.2828 31.6223 49.2073C33.4901 49.8548 35.471 50.4205 37.5379 50.8841C38.6438 51.1321 39.74 51.3431 40.8214 51.5182C41.6228 51.064 42.4312 50.3683 43.2606 49.3747C41.542 49.1412 39.8033 48.83 38.0849 48.4446C34.4109 47.6207 31.7817 46.6714 29.9426 45.5922ZM42.1023 30.5307C44.9993 31.1804 47.8468 32.0984 50.4676 33.1911C50.5975 32.6246 50.7121 32.0672 50.8105 31.5214C48.2623 30.4846 45.4519 29.6 42.4614 28.9294C40.8047 28.5578 39.1699 28.2693 37.5742 28.0602C37.1482 28.5885 36.7277 29.1386 36.3152 29.7072C37.9544 29.7669 39.8669 30.0294 42.1023 30.5307Z"
          fill="url(#paint2_linear_1058_474)"
        />
      </g>
      <g style="mix-blend-mode:plus-lighter" filter="url(#filter3_b_1058_474)">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M61.1415 37.8392C59.6784 36.417 57.7855 35.0451 55.5537 33.7915C55.4257 34.3412 55.2862 34.8967 55.1353 35.4571C57.6921 36.9001 59.8156 38.4905 61.2498 40.0929C61.2368 43.1147 60.5932 45.9875 59.4437 48.5867C56.8648 49.5829 53.1902 49.9894 49.0832 49.8666C48.6271 50.6546 48.1597 51.4207 47.683 52.1633C51.7996 52.2555 55.4668 51.7678 58.3194 50.7751C54.6243 57.0438 47.8033 61.2499 40 61.2499C39.0315 61.2499 38.0782 61.1851 37.1441 61.0596C41.6654 58.193 46.532 52.3113 50.1451 44.765C53.7026 37.3348 55.241 29.9698 54.713 24.6672C58.2543 28.0663 60.6206 32.6803 61.1415 37.8392ZM52.8354 23.0628C50.4974 21.2883 47.7808 19.9868 44.8281 19.3007C40.2467 21.8852 34.8475 28.4137 31.3314 35.7572C26.2387 46.3939 29.2293 53.9585 34.0158 56.2502C38.8023 58.5419 42.7975 54.3221 47.8902 43.6854C51.4239 36.3049 53.4965 28.1374 52.8354 23.0628ZM40 14.9999C43.495 14.9999 46.8225 15.7171 49.8433 17.0123C50.1708 17.0965 50.4882 17.2115 50.7943 17.358C51.0965 17.5028 51.3816 17.6753 51.6495 17.8743C59.5886 22.063 65 30.3995 65 39.9999C65 53.807 53.8071 64.9999 40 64.9999C26.1929 64.9999 15 53.807 15 39.9999C15 38.2384 15.1822 36.5194 15.5287 34.8608C15.5483 34.7186 15.5739 34.577 15.6055 34.4361C15.6397 34.2836 15.6807 34.1334 15.7282 33.9854C18.4207 23.0834 28.2663 14.9999 40 14.9999ZM21.8211 28.9898C25.547 22.8511 32.2945 18.7499 40 18.7499C40.8843 18.7499 41.7561 18.8039 42.612 18.9088C38.1593 21.829 33.4004 27.6357 29.8513 35.0485C26.2481 42.5741 24.7162 50.0329 25.3047 55.3496C21.7067 51.904 19.3179 47.2059 18.8388 41.9558C20.2881 43.3686 22.1615 44.732 24.3702 45.9795C24.5834 45.0963 24.8258 44.1989 25.0975 43.2905C22.8238 41.4453 21.9532 39.3117 22.4996 36.8753C23.2112 33.7023 26.0904 31.0667 30.861 30.095C31.3517 29.2545 31.8551 28.4393 32.3689 27.6513C28.2986 27.5553 24.6654 28.0255 21.8211 28.9898ZM30.0059 46.0295C30.2386 47.3468 30.8152 48.373 31.6223 49.2073C33.4901 49.8548 35.471 50.4205 37.5379 50.8841C38.6438 51.1321 39.74 51.3431 40.8214 51.5182C41.6228 51.064 42.4312 50.3683 43.2606 49.3747C41.542 49.1412 39.8033 48.83 38.0849 48.4446C34.9155 47.7339 32.2216 46.9298 30.0059 46.0295ZM42.1023 30.5307C44.9993 31.1804 47.8468 32.0984 50.4676 33.1911C50.5975 32.6246 50.7121 32.0672 50.8105 31.5214C48.2623 30.4846 45.4519 29.6 42.4614 28.9294C40.8047 28.5578 39.1699 28.2693 37.5742 28.0602C37.1445 28.5931 36.7204 29.1481 36.3045 29.722C38.0822 29.8032 40.0171 30.0631 42.1023 30.5307Z"
          fill="url(#paint3_linear_1058_474)"
        />
      </g>
      <defs>
        <filter
          id="filter0_f_1058_474"
          x="0"
          y="-0.00012207"
          width="80"
          height="80"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="7.5"
            result="effect1_foregroundBlur_1058_474"
          />
        </filter>
        <filter
          id="filter1_f_1058_474"
          x="0"
          y="-0.00012207"
          width="80"
          height="80"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="7.5"
            result="effect1_foregroundBlur_1058_474"
          />
        </filter>
        <filter
          id="filter2_f_1058_474"
          x="7.5"
          y="7.49988"
          width="65"
          height="65"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="3.75"
            result="effect1_foregroundBlur_1058_474"
          />
        </filter>
        <filter
          id="filter3_b_1058_474"
          x="2.5"
          y="2.49988"
          width="75"
          height="75"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feGaussianBlur in="BackgroundImageFix" stdDeviation="6.25" />
          <feComposite
            in2="SourceAlpha"
            operator="in"
            result="effect1_backgroundBlur_1058_474"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_backgroundBlur_1058_474"
            result="shape"
          />
        </filter>
        <linearGradient
          id="paint0_linear_1058_474"
          x1="45.6349"
          y1="-40.3667"
          x2="-24.6134"
          y2="-7.46718"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#A6A9FA" />
          <stop offset="0.376705" stop-color="#FC9D67" />
          <stop offset="0.74996" stop-color="#B145C2" />
          <stop offset="1" stop-color="#000AFF" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_1058_474"
          x1="45.6349"
          y1="-40.3667"
          x2="-24.6134"
          y2="-7.46718"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#A6A9FA" />
          <stop offset="0.376705" stop-color="#FC9D67" />
          <stop offset="0.74996" stop-color="#B145C2" />
          <stop offset="1" stop-color="#000AFF" />
        </linearGradient>
        <linearGradient
          id="paint2_linear_1058_474"
          x1="45.6349"
          y1="-40.3667"
          x2="-24.6134"
          y2="-7.46718"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#A6A9FA" />
          <stop offset="0.376705" stop-color="#FC9D67" />
          <stop offset="0.74996" stop-color="#B145C2" />
          <stop offset="1" stop-color="#000AFF" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_1058_474"
          x1="45.6349"
          y1="-40.3667"
          x2="-24.6134"
          y2="-7.46718"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#A6A9FA" />
          <stop offset="0.376705" stop-color="#FC9D67" />
          <stop offset="0.74996" stop-color="#B145C2" />
          <stop offset="1" stop-color="#000AFF" />
        </linearGradient>
      </defs>
    </svg>
  );
}
